import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useParams, Link, useNavigate } from "react-router-dom";
import { adminApi, fileUrl } from "../../lib/api";
import { useDialog } from "../../lib/dialog";
import { useSettings } from "../../lib/settings";
import { useEvents } from "../../lib/events";
import AdminLayout from "../../components/app/AdminLayout";
import AvailabilityCalendar from "../../components/app/AvailabilityCalendar";
import FieldRenderer from "../../components/app/FieldRenderer";
import { ABILITY_LABELS } from "../../lib/scoring";
import { AssignDatesModal } from "../../components/app/AssignModals";
import { Loader } from "./Dashboard";
import { ArrowLeft, Clock, User, Send, Trash2, ExternalLink, Edit3, Save, X as XIcon, ImageIcon, Check, Loader2, Drama } from "lucide-react";

const STATIC_TABS = ["profile", "auditions", "notes", "messages", "history"];

function SuitabilityBadge({ match }) {
  if (!match) return null;
  if (!match.has_profile) return <span className="text-[10px] text-[#4a4843]" title="No ideal ability profile set on this role">no profile set</span>;
  if (match.suitability == null) return <span className="text-[10px] text-[#4a4843]">not yet scored</span>;
  const s = match.suitability;
  const color = s >= 80 ? "#22C55E" : s >= 55 ? "#F59E0B" : "#EF4444";
  const partial = match.required > 0 && match.covered < match.required;
  return (
    <span className="inline-flex items-center gap-1.5" title={`${match.covered}/${match.required} required abilities scored`}>
      <span className="w-14 h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden"><span className="block h-full rounded-full" style={{ width: `${s}%`, background: color }} /></span>
      <span className="text-xs font-mono font-bold" style={{ color }}>{s}%</span>
      {partial && <span className="text-[9px] text-[#6B6862]">({match.covered}/{match.required})</span>}
    </span>
  );
}

export default function ApplicantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dialog = useDialog();
  const { formatDate, formatLong } = useSettings();
  const { isArchived } = useEvents();

  const [a, setA] = useState(null);
  const [decline, setDecline] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [formSchema, setFormSchema] = useState(null);
  const [showDates, setShowDates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tab, setTab] = useState("profile");
  const [noteText, setNoteText] = useState("");
  const [msgSubject, setMsgSubject] = useState("");
  const [msgBody, setMsgBody] = useState("");

  // Audition invite modal (Update 10)
  const [showInvite, setShowInvite] = useState(false);
  const [showCantAttend, setShowCantAttend] = useState(false);
  const [auditions, setAuditions] = useState([]);
  const [inviteSel, setInviteSel] = useState([]);
  const [inviting, setInviting] = useState(false);
  // Switch/edit confirmed audition date
  const [switchTo, setSwitchTo] = useState("");
  const [switchEmail, setSwitchEmail] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [scores, setScores] = useState(null);
  const [matches, setMatches] = useState(null);
  const matchFor = (rtid) => (matches?.items || []).find((m) => m.role_type_id === rtid);
  const [prefAssignRole, setPrefAssignRole] = useState(null);
  const [castBusy, setCastBusy] = useState(false);

  const confirmPrefAssign = async ({ date_ids, send_email, preliminary }) => {
    await adminApi.post(`/admin/roles/${prefAssignRole.id}/assign-dates`, { actor_id: id, date_ids, send_email, preliminary });
    setPrefAssignRole(null);
    load();
    loadRoles();
    adminApi.get(`/admin/applicants/${id}/role-matches`).then(({ data }) => setMatches(data)).catch(() => {});
  };
  const roleForPref = (rid) => roles.find((r) => (r.role_type_id || r.id) === rid);

  // Edit mode (Change 01)
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.get(`/admin/applicants/${id}`).then(({ data }) => setA(data));
  const loadRoles = () => adminApi.get("/admin/roles").then(({ data }) => setRoles(data.items || []));
  const loadDecline = () => adminApi.get("/admin/role-declines").then(({ data }) => setDecline((data.items || []).find((d) => d.actor_id === id) || null));
  useEffect(() => {
    load();
    loadRoles();
    loadDecline();
    adminApi.get("/admin/statuses").then(({ data }) => setStatuses(data.items));
    adminApi.get("/admin/forms").then(({ data }) => { if (data.items.length) setFormSchema(data.items[0]); });
    adminApi.get("/admin/show-dates").then(({ data }) => setShowDates(data.items || []));
    adminApi.get("/admin/auditions").then(({ data }) => setAuditions(data.items || []));
    adminApi.get(`/admin/applicants/${id}/scores`).then(({ data }) => setScores(data)).catch(() => setScores(null));
    adminApi.get(`/admin/applicants/${id}/role-matches`).then(({ data }) => setMatches(data)).catch(() => setMatches(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Custom tabs derived from form schema (Change 05)
  const customTabs = useMemo(() => {
    if (!formSchema?.fields) return [];
    const orderMap = new Map();
    formSchema.fields.forEach((f, i) => {
      if (f.profile_tab && f.type !== "text_block") {
        if (!orderMap.has(f.profile_tab)) orderMap.set(f.profile_tab, i);
      }
    });
    return Array.from(orderMap.entries())
      .sort((x, y) => x[1] - y[1])
      .map(([name]) => name);
  }, [formSchema]);

  const allTabs = [...STATIC_TABS.slice(0, 1), ...customTabs, ...STATIC_TABS.slice(1), "assign role"];

  // Future audition dates available to invite to (Update 10)
  const futureAuditions = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return (auditions || []).filter((au) => au.type !== "dress_rehearsal" && au.date >= today);
  }, [auditions]);

  // Audition invite button state (Update 14 Change 02): three states.
  const auditionAccepted = ["audition_accepted", "audition_booked"].includes(a?.status);
  const inviteSent = (a?.audition_invite_sent) || a?.status === "audition_offered";

  if (!a) return <AdminLayout title="Loading…"><Loader /></AdminLayout>;

  const status = statuses.find((s) => s.key === a.status);
  const ASSIGNABLE = ["offered_role", "contracted"];
  const canSendContract = ASSIGNABLE.includes(a.status);
  const hasRoleAssigned = roles.some((r) => (r.assignments || []).some((x) => x.actor_id === a.id));
  const castEligible = ["audition_attended", "offered_role", "contracted"].includes(a.status);

  const toggleCast = async () => {
    if (a.in_cast) {
      if (!await dialog.confirm({ title: "Remove from cast?", description: "This removes them from the cast pool and clears any preliminary (un-invited) role assignments. Invited/confirmed roles are kept.", confirmLabel: "Remove from Cast", destructive: true })) return;
    }
    setCastBusy(true);
    try {
      if (a.in_cast) await adminApi.delete(`/admin/applicants/${id}/cast`);
      else await adminApi.post(`/admin/applicants/${id}/cast`);
      await load(); await loadRoles();
    } finally { setCastBusy(false); }
  };

  // --- Mutations using dialog confirmations ---
  const updateStatus = async (newStatus) => {
    const lbl = statuses.find((s) => s.key === newStatus)?.label;
    if (!await dialog.confirm({ title: `Change status to "${lbl}"?`, description: "This may send an automated email to the applicant.", confirmLabel: "Change Status", destructive: false })) return;
    await adminApi.patch(`/admin/applicants/${id}/status`, { status: newStatus });
    load();
  };

  const sendContract = async () => {
    if (!await dialog.confirm({ title: a.contract_sent_at ? "Resend contract?" : "Send contract?", description: `Email a secure link to ${a.full_name} to review and digitally accept their contract.`, confirmLabel: a.contract_sent_at ? "Resend Contract" : "Send Contract" })) return;
    try {
      await adminApi.post(`/admin/applicants/${id}/send-contract`);
      await load();
      await dialog.alert({ title: "Contract sent", description: `${a.full_name} has been emailed a link to review and accept their contract.` });
    } catch (e) {
      await dialog.alert({ title: "Could not send contract", description: e?.response?.data?.detail || "Please try again." });
    }
  };

  const resolveDecline = async (action) => {
    const cfg = action === "remove"
      ? { title: "Remove role & dates?", description: `This removes ${a.full_name} from all assigned roles/dates and marks them Rejected. This cannot be undone.`, confirmLabel: "Remove", destructive: true }
      : { title: "Offer a new role?", description: `This frees ${a.full_name}'s declined role and keeps them as 'Offered a Role' so you can assign a different one.`, confirmLabel: "Continue" };
    if (!await dialog.confirm(cfg)) return;
    await adminApi.post(`/admin/role-declines/${decline.id}/resolve`, { action });
    await load(); await loadRoles(); await loadDecline();
    if (action === "reoffer") setTab("assign role");
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await adminApi.post(`/admin/applicants/${id}/notes`, { text: noteText });
    setNoteText(""); load();
  };

  const sendMessage = async () => {
    if (!msgSubject.trim() || !msgBody.trim()) return;
    await adminApi.post(`/admin/applicants/${id}/messages`, { subject: msgSubject, body: msgBody });
    setMsgSubject(""); setMsgBody(""); load();
  };

  const remove = async () => {
    if (!await dialog.confirm({
      title: "Permanently delete this record?",
      description: "This will completely remove this applicant from the system, including all their application data and any talent pool entry. This cannot be undone.",
      confirmLabel: "Delete Permanently",
      cancelLabel: "Cancel",
      destructive: true,
    })) return;
    await adminApi.delete(`/admin/applicants/${id}`);
    navigate("/admin/applicants");
  };

  const rejectApplication = async () => {
    const choice = await dialog.actions({
      title: "Reject this application?",
      description: "This will mark the applicant as rejected. Would you also like to retain them in the talent pool for future seasons?",
      destructive: true,
      cancelLabel: "Cancel",
      actions: [
        { label: "Reject Only", value: "reject_only", variant: "secondary" },
        { label: "Reject & Keep in Talent Pool", value: "reject_keep", variant: "primary" },
      ],
    });
    if (!choice) return;
    const keepInPool = choice === "reject_keep";
    await adminApi.patch(`/admin/applicants/${id}/status`, { status: "rejected", in_talent_pool: keepInPool });
    load();
  };

  // --- Update 24: virtual audition attendance ---
  const markVirtualAttended = async () => {
    if (!await dialog.confirm({
      title: `Mark ${a.full_name} as having attended a virtual audition?`,
      description: "Their status will move to Audition Attended and the normal post-audition options (Assign Role / Decline) will appear.",
      confirmLabel: "Confirm",
    })) return;
    setShowCantAttend(false);
    await adminApi.post(`/admin/applicants/${id}/mark-virtual-attended`);
    load();
  };

  const declineApplicant = async () => {
    const choice = await dialog.actions({
      title: "Decline this applicant?",
      description: "This will mark the applicant as Rejected. Would you also like to retain them in the talent pool for future seasons?",
      destructive: true,
      cancelLabel: "Cancel",
      actions: [
        { label: "Decline Only", value: "reject_only", variant: "secondary" },
        { label: "Decline & Keep in Talent Pool", value: "reject_keep", variant: "primary" },
      ],
    });
    if (!choice) return;
    await adminApi.patch(`/admin/applicants/${id}/status`, { status: "rejected", in_talent_pool: choice === "reject_keep" });
    load();
  };

  // --- Audition invite (Update 10) ---
  const openInvite = () => { setInviteSel([]); setShowInvite(true); };
  const toggleInvite = (aid) => setInviteSel((s) => s.includes(aid) ? s.filter((x) => x !== aid) : [...s, aid]);
  const sendInvite = async () => {
    if (inviteSel.length === 0) return;
    setInviting(true);
    try {
      await adminApi.post(`/admin/applicants/${id}/invite-audition`, { audition_date_ids: inviteSel });
      setShowInvite(false);
      load();
    } catch (e) {
      await dialog.alert({ title: "Could not send invite", description: e?.response?.data?.detail || "Please try again." });
    } finally {
      setInviting(false);
    }
  };

  const doSwitchAudition = async () => {
    if (!switchTo) return;
    setSwitching(true);
    try {
      await adminApi.post(`/admin/applicants/${id}/switch-audition`, { audition_date_id: switchTo, send_email: switchEmail });
      setSwitchTo("");
      await load();
      await dialog.alert({ title: "Audition date switched", description: `This applicant is now confirmed for the new date.${switchEmail ? " An Audition Confirmed email has been sent with the new details." : ""}` });
    } catch (e) {
      await dialog.alert({ title: "Could not switch date", description: e?.response?.data?.detail || "Please try again." });
    } finally {
      setSwitching(false);
    }
  };

  const resendInvite = async () => {
    const ok = await dialog.confirm({
      title: "Resend audition invite?",
      description: `An audition invite has already been sent to ${a.full_name}. Would you like to resend it? A fresh link will be generated and the previous one will stop working.`,
      confirmLabel: "Resend Invite",
      cancelLabel: "Cancel",
    });
    if (!ok) return;
    const dateIds = a.audition_invite_date_ids || [];
    if (dateIds.length === 0) { openInvite(); return; }
    try {
      await adminApi.post(`/admin/applicants/${id}/invite-audition`, { audition_date_ids: dateIds, resend: true });
      load();
    } catch (e) {
      await dialog.alert({ title: "Could not resend invite", description: e?.response?.data?.detail || "The original audition date(s) may have passed — please send a new invite." });
    }
  };

  // --- Edit mode helpers ---
  const beginEdit = () => {
    setDraft({ ...a });
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setDraft({});
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      // Only include changed fields
      const payload = {};
      Object.keys(draft).forEach((k) => {
        if (JSON.stringify(draft[k]) !== JSON.stringify(a[k])) payload[k] = draft[k];
      });
      await adminApi.patch(`/admin/applicants/${id}`, payload);
      await load();
      setEditing(false); setDraft({});
    } finally { setSaving(false); }
  };
  const updDraft = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  // Age helper for header (Change 01)
  const ageNow = (() => {
    if (!a.date_of_birth) return null;
    try {
      const d = new Date(a.date_of_birth);
      return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 365.25));
    } catch { return null; }
  })();

  return (
    <AdminLayout
      title={
        <span className="inline-flex items-baseline gap-3" data-testid="applicant-header">
          <span>{a.full_name}</span>
          {(ageNow !== null || a.gender) && (
            <span className="font-mono text-base font-normal text-[#A19D94] tracking-wider">
              {ageNow !== null ? ageNow : ""}{ageNow !== null && a.gender ? " · " : ""}{a.gender || ""}
            </span>
          )}
        </span>
      }
      subtitle={null}
      actions={
        <div className="flex items-center gap-2">
          {!editing && castEligible && !isArchived && (
            a.in_cast ? (
              <button onClick={toggleCast} disabled={castBusy} data-testid="remove-from-cast-btn"
                className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#22C55E]/50 text-[#22C55E] hover:bg-[#22C55E]/10 disabled:opacity-40">
                {castBusy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} In Cast
              </button>
            ) : (
              <button onClick={toggleCast} disabled={castBusy} data-testid="add-to-cast-btn"
                className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood disabled:opacity-40">
                {castBusy ? <Loader2 size={14} className="animate-spin" /> : <Drama size={14} />} Add to Cast
              </button>
            )
          )}
          {!editing && tab === "profile" && !isArchived && (
            <button onClick={beginEdit} data-testid="edit-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood">
              <Edit3 size={14} /> Edit
            </button>
          )}
          {editing && (
            <>
              <button onClick={cancelEdit} data-testid="cancel-edit-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-[#3a3835] hover:text-[#F2EBE1]">
                <XIcon size={14} /> Cancel
              </button>
              <button onClick={saveEdit} disabled={saving} data-testid="save-edit-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40">
                <Save size={14} /> {saving ? "Saving…" : "Save Changes"}
              </button>
            </>
          )}
          <Link to="/admin/applicants" className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#A19D94] hover:text-blood">
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      }
    >
      <div className="flex gap-2 mb-6 -mt-3 border-b border-[#1a1a1a] overflow-x-auto">
        {allTabs.map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); if (editing && t !== "profile") cancelEdit(); }}
            data-testid={`tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
            className={`px-4 py-2.5 text-xs uppercase tracking-[0.18em] border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === t ? "border-blood text-[#F2EBE1]" : "border-transparent text-[#8a857d] hover:text-[#F2EBE1]"
            }`}
          >{t}</button>
        ))}
      </div>

      {tab === "assign role" ? (
        <AssignRoleTab applicant={a} roles={roles} showDates={showDates} reloadRoles={loadRoles} reloadApplicant={load} isArchived={isArchived} />
      ) : (
      <div className="grid lg:grid-cols-12 gap-4">
        {/* Left col: photo & key info */}
        <div className="lg:col-span-4 space-y-4">
          <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-4">
            {a.headshot_file_id ? (
              <img src={fileUrl(a.headshot_file_id)} alt={a.full_name} className="w-full aspect-square object-cover border border-[#262626] mb-3" />
            ) : (
              <div className="w-full aspect-square bg-[#1a1a1a] border border-[#262626] flex items-center justify-center mb-3">
                <User size={64} className="text-[#3a3835]" strokeWidth={1} />
              </div>
            )}
            <div className="flex items-center gap-2 text-xs">
              <span className="w-2 h-2 rounded-full" style={{ background: status?.color || "#6B6862" }} />
              <span className="font-display uppercase tracking-wider">{status?.label || a.status}</span>
            </div>
          </div>

          <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-4 space-y-2">
            <h3 className="label-mini mb-3">Personal Details</h3>
            <InfoRow label="Full Name" value={a.full_name} />
            <InfoRow label="Email" value={a.email} />
            <InfoRow label="Phone" value={a.phone} mono />
            <InfoRow label="Date of Birth" value={a.date_of_birth ? `${formatDate(a.date_of_birth)} (age ${Math.max(0, Math.floor((Date.now() - new Date(a.date_of_birth).getTime()) / 31557600000))})` : "—"} />
            {a.home_address && <InfoRow label="Home Address" value={a.home_address} multiline />}
            {(a.emergency_contact_name || a.emergency_contact_phone) && (
              <InfoRow label="Emergency Contact" value={[a.emergency_contact_name, a.emergency_contact_phone].filter(Boolean).join(" · ")} multiline />
            )}
            <div className="pt-2 mt-2 border-t border-[#1a1a1a] space-y-2">
              <InfoRow label="Date Submitted" value={formatDate(a.submitted_at)} />
            </div>
          </div>
        </div>

        {/* Middle col */}
        <div className="lg:col-span-5 space-y-4">
          {tab === "profile" && (
            <DynamicApplicationView
              applicant={a}
              schema={formSchema}
              editing={editing}
              draft={draft}
              updDraft={updDraft}
              showDates={showDates}
              tabFilter={null}
            />
          )}

          {customTabs.includes(tab) && (
            <DynamicApplicationView
              applicant={a}
              schema={formSchema}
              editing={editing}
              draft={draft}
              updDraft={updDraft}
              showDates={showDates}
              tabFilter={tab}
            />
          )}

          {tab === "auditions" && (
            <div className="space-y-4">
              <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-5">
                <h3 className="label-mini mb-3">Audition Invite</h3>
                {!inviteSent && !auditionAccepted ? (
                  <p className="text-sm text-[#6B6862]">No audition invite sent yet. Use the "Invite to Audition" button to send one.</p>
                ) : (
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs uppercase tracking-wider ${auditionAccepted ? "bg-[#16A34A]/15 text-[#22C55E] border border-[#16A34A]/40" : "bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/40"}`}>
                      {auditionAccepted ? <><Check size={12} /> Accepted</> : "Invite Sent — awaiting response"}
                    </span>
                  </div>
                )}
              </div>
              <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-5">
                <h3 className="label-mini mb-3">Confirmed Audition Date{(a.selected_audition_dates || []).length > 1 ? "s" : ""}</h3>
                {(a.selected_audition_dates || []).length === 0 ? (
                  <p className="text-sm text-[#6B6862]">No audition date confirmed yet.</p>
                ) : (
                  <ul className="space-y-3">
                    {(a.selected_audition_dates || []).map((aid) => {
                      const au = auditions.find((x) => x.id === aid);
                      const att = (a.audition_attendance || {})[aid];
                      const statusLabel = att === "attended" ? "Attended" : att === "no_show" ? "No-show" : "Confirmed";
                      const statusColor = att === "attended" ? "text-[#22C55E]" : att === "no_show" ? "text-blood" : "text-[#A19D94]";
                      return (
                        <li key={aid} className="border border-[#1a1a1a] p-3" data-testid={`audition-confirmed-${aid}`}>
                          {au ? (
                            <>
                              <div className="flex items-center justify-between gap-2">
                                <div className="font-display uppercase tracking-tight">{au.name}</div>
                                <span className={`text-xs uppercase tracking-wider ${statusColor}`}>{statusLabel}</span>
                              </div>
                              <div className="text-xs text-[#A19D94] mt-1">{formatLong(au.date)} · {au.start_time}–{au.end_time}</div>
                              {au.location && <div className="text-xs text-[#A19D94] mt-0.5">{au.location}</div>}
                            </>
                          ) : (
                            <div className="text-sm text-[#6B6862]">Audition date no longer exists.</div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {(a.preferred_role_ids || []).length > 0 && (
                <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-5" data-testid="preferred-roles-panel">
                  <h3 className="label-mini mb-3">Preferred Roles <span className="text-[#4a4843]">· chosen at audition</span></h3>
                  <div className="space-y-2">
                    {(a.preferred_role_ids || []).map((rid, i) => {
                      const role = roleForPref(rid);
                      const name = role ? (role.base_name || role.name) : "Unknown role";
                      const sl = role?.skill_level;
                      const assigned = role && (role.assignments || []).some((x) => x.actor_id === id);
                      return (
                        <div key={rid} className="flex items-center gap-3 border border-[#1a1a1a] bg-[#050505] px-3 py-2.5" data-testid={`preferred-role-${rid}`}>
                          <span className="font-mono text-blood text-xs w-4 shrink-0">{i + 1}</span>
                          <div className="w-11 h-11 shrink-0 border border-[#262626] bg-[#0A0A0A] overflow-hidden flex items-center justify-center">
                            {role?.profile_image ? <img src={fileUrl(role.profile_image)} alt={name} className="w-full h-full object-cover" /> : <ImageIcon size={16} className="text-[#3a3835]" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-display uppercase tracking-tight truncate leading-tight">{name}</div>
                            <div className="flex items-center gap-2 mt-0.5">
                              {sl ? <span className="text-[10px] text-[#6B6862]">Skill level {sl}</span> : null}
                              <SuitabilityBadge match={matchFor(rid)} />
                            </div>
                          </div>
                          {!isArchived && role && (
                            assigned ? (
                              <span className="shrink-0 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#22C55E]" data-testid={`preferred-role-assigned-${rid}`}><Check size={13} /> Assigned</span>
                            ) : (
                              <button onClick={() => setPrefAssignRole(role)} data-testid={`preferred-role-assign-${rid}`}
                                className="shrink-0 px-3 py-1.5 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] inline-flex items-center gap-1.5">
                                <Check size={12} /> Assign Role
                              </button>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {matches && matches.has_scores && (matches.items || []).some((m) => m.has_profile && m.suitability != null && m.gender_ok) && (
                <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-5" data-testid="suggested-matches-panel">
                  <h3 className="label-mini mb-1">Suggested Best Matches</h3>
                  <p className="text-[11px] text-[#6B6862] mb-3">Ranked across all roles by how well their audition scores fit each role's ideal ability profile.</p>
                  <div className="space-y-2">
                    {(matches.items || []).filter((m) => m.has_profile && m.suitability != null && m.gender_ok).slice(0, 5).map((m, i) => (
                      <div key={m.role_type_id} className="flex items-center gap-3 border border-[#1a1a1a] bg-[#050505] px-3 py-2.5" data-testid={`suggested-match-${m.role_type_id}`}>
                        <span className="font-mono text-[#6B6862] text-xs w-4 shrink-0">{i + 1}</span>
                        <div className="w-11 h-11 shrink-0 border border-[#262626] bg-[#0A0A0A] overflow-hidden flex items-center justify-center">
                          {m.profile_image ? <img src={fileUrl(m.profile_image)} alt={m.name} className="w-full h-full object-cover" /> : <ImageIcon size={16} className="text-[#3a3835]" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display uppercase tracking-tight truncate leading-tight flex items-center gap-2">
                            {m.name}
                            {m.is_preferred && <span className="text-[9px] uppercase tracking-wider text-blood border border-blood/40 px-1.5 py-0.5">Their pick</span>}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5"><SuitabilityBadge match={m} /></div>
                        </div>
                        {!isArchived && (
                          m.already_assigned ? (
                            <span className="shrink-0 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#22C55E]"><Check size={13} /> Assigned</span>
                          ) : (
                            <button onClick={() => { const r = roles.find((x) => (x.role_type_id || x.id) === m.role_type_id); if (r) setPrefAssignRole(r); }}
                              data-testid={`suggested-assign-${m.role_type_id}`}
                              className="shrink-0 px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood inline-flex items-center gap-1.5">
                              <Check size={12} /> Assign
                            </button>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {scores && scores.judges_scored > 0 && (
                <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-5" data-testid="scores-panel">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="label-mini">Audition Scores</h3>
                    <div className="text-right">
                      <div className="font-display text-3xl text-blood leading-none" data-testid="scores-overall">{scores.overall ?? "—"}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[#6B6862]">Overall avg · {scores.judges_scored} judge{scores.judges_scored > 1 ? "s" : ""}</div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse" data-testid="scores-matrix">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6862] border-b border-[#1a1a1a]">
                          <th className="py-2 pr-3 font-normal">Ability</th>
                          {scores.judges.map((j) => (
                            <th key={j.judge_id} className="py-2 px-2 font-normal text-center whitespace-nowrap">{j.judge_name}</th>
                          ))}
                          <th className="py-2 pl-2 font-normal text-center text-blood">Avg</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(scores.abilities || []).map((ab) => (
                          <tr key={ab} className="border-b border-[#141414]">
                            <td className="py-1.5 pr-3 text-[#A19D94] whitespace-nowrap">{ABILITY_LABELS[ab] || ab}</td>
                            {scores.judges.map((j) => (
                              <td key={j.judge_id} className="py-1.5 px-2 text-center font-mono text-[#F2EBE1]">{j.scores?.[ab] ?? "—"}</td>
                            ))}
                            <td className="py-1.5 pl-2 text-center font-mono text-blood">{scores.per_ability?.[ab] ?? "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {scores.judges.some((j) => (j.notes || "").trim()) && (
                    <div className="mt-4 space-y-3">
                      <h4 className="label-mini">Judge Notes</h4>
                      {scores.judges.filter((j) => (j.notes || "").trim()).map((j) => (
                        <div key={j.judge_id} className="border border-[#1a1a1a] p-3" data-testid={`scores-note-${j.judge_id}`}>
                          <div className="text-[11px] uppercase tracking-wider text-[#6B6862] mb-1">{j.judge_name}</div>
                          <p className="text-sm text-[#A19D94] whitespace-pre-wrap">{j.notes}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!isArchived && futureAuditions.length > 0 && !Object.values(a.audition_attendance || {}).includes("attended") && (
                <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-5" data-testid="switch-audition-panel">
                  <h3 className="label-mini mb-2">Switch / Edit Audition Date</h3>
                  <p className="text-xs text-[#6B6862] mb-3">Move this applicant to a different audition date (e.g. they can no longer make their original date). They'll be set to "Audition Accepted" for the new date.</p>
                  <select value={switchTo} onChange={(e) => setSwitchTo(e.target.value)} data-testid="switch-audition-select"
                    className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood">
                    <option value="">Select new audition date…</option>
                    {futureAuditions.map((au) => (
                      <option key={au.id} value={au.id}>{formatLong(au.date)} — {au.name}{au.location ? ` (${au.location})` : ""}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-2 mt-3 cursor-pointer" data-testid="switch-audition-email">
                    <input type="checkbox" checked={switchEmail} onChange={(e) => setSwitchEmail(e.target.checked)} className="accent-[#D91616]" />
                    <span className="text-xs text-[#A19D94]">Send the Audition Confirmed email with the new date details</span>
                  </label>
                  <button onClick={doSwitchAudition} disabled={!switchTo || switching} data-testid="switch-audition-save"
                    className="mt-3 w-full px-4 py-2.5 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40 flex items-center justify-center gap-2">
                    {switching ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Switch Date
                  </button>
                </div>
              )}
            </div>
          )}

          {tab === "notes" && (
            <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-5">
              <h3 className="label-mini mb-3">Internal Notes</h3>
              <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
                {(a.notes || []).length === 0 && <p className="text-sm text-[#6B6862]">No notes yet.</p>}
                {(a.notes || []).map((n) => (
                  <div key={n.id} className="border-l-2 border-blood pl-3 py-1">
                    <div className="text-sm">{n.text}</div>
                    <div className="text-[10px] font-mono text-[#6B6862] mt-1">{n.by_name || n.by} · {formatDate(n.created_at, "datetime")}</div>
                  </div>
                ))}
              </div>
              <textarea
                data-testid="note-input"
                value={noteText} onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a private note…" rows={2}
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-blood resize-none"
              />
              <button onClick={addNote} data-testid="add-note-btn" className="mt-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">Add Note</button>
            </div>
          )}

          {tab === "messages" && (
            <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-5">
              <h3 className="label-mini mb-3">Messages Sent</h3>
              <div className="space-y-3 mb-4 max-h-72 overflow-y-auto">
                {(a.messages || []).length === 0 && <p className="text-sm text-[#6B6862]">No messages sent.</p>}
                {(a.messages || []).map((m) => (
                  <div key={m.id} className="border border-[#262626] p-3">
                    <div className="text-sm font-semibold">{m.subject}</div>
                    <div className="text-xs text-[#A19D94] mt-1" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.body || "") }} />
                    <div className="text-[10px] font-mono text-[#6B6862] mt-2">{formatDate(m.created_at, "datetime")} · {m.result?.status}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <input data-testid="msg-subject" value={msgSubject} onChange={(e) => setMsgSubject(e.target.value)} placeholder="Subject" className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-blood" />
                <textarea data-testid="msg-body" value={msgBody} onChange={(e) => setMsgBody(e.target.value)} placeholder="Message (HTML supported)" rows={4} className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-blood resize-none" />
                <button onClick={sendMessage} data-testid="send-msg-btn" className="px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] flex items-center gap-2"><Send size={12} /> Send</button>
              </div>
            </div>
          )}

          {tab === "history" && (
            <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-5">
              <h3 className="label-mini mb-3">Audit Log</h3>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {(a.audit_log || []).slice().reverse().map((log, i) => (
                  <div key={`${log.timestamp}-${log.action}-${i}`} className="flex gap-3 py-2 border-b border-[#1a1a1a] last:border-0">
                    <Clock size={12} className="text-blood mt-1 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm uppercase tracking-wider">{log.action.replace(/_/g, " ")}</div>
                      <div className="text-xs text-[#A19D94] mt-0.5 break-words">{log.details}</div>
                      <div className="text-[10px] font-mono text-[#6B6862] mt-1">{formatDate(log.timestamp, "datetime")} · {log.by}</div>
                    </div>
                  </div>
                ))}
                {(a.audit_log || []).length === 0 && <p className="text-sm text-[#6B6862]">No activity yet.</p>}
              </div>
            </div>
          )}
        </div>

        {/* Right col: Status, Actions, then Calendar (Change 04 placement) */}
        <div className="lg:col-span-3 space-y-4">
          <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="label-mini">Status & Actions</h3>
              {!isArchived && (
                <button onClick={remove} data-testid="delete-btn" title="Delete record" className="p-1.5 text-[#6B6862] hover:text-blood transition-colors">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <label className="label-mini block mb-1.5">Status</label>
            <select data-testid="status-select" value={a.status} disabled={isArchived} onChange={(e) => updateStatus(e.target.value)}
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood disabled:opacity-50">
              {statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>

            {/* Contract status indicator (Update 18) */}
            {a.contract_accepted_at ? (
              <div data-testid="contract-status-accepted" className="mt-3 px-3 py-2 text-xs bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/40 flex items-center gap-1.5">
                <Check size={13} /> Contract accepted · {new Date(a.contract_accepted_at).toLocaleString()}
              </div>
            ) : a.contract_sent_at ? (
              <div data-testid="contract-status-sent" className="mt-3 px-3 py-2 text-xs bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/40">
                Contract sent — awaiting acceptance · {new Date(a.contract_sent_at).toLocaleDateString()}
              </div>
            ) : canSendContract && hasRoleAssigned ? (
              <div data-testid="contract-status-prompt" className="mt-3 px-3 py-2 text-xs text-[#A19D94] border border-[#262626]">
                Role assigned — contract not yet sent
              </div>
            ) : null}

            {/* Role declined notification (Update 20) */}
            {decline && (
              <div data-testid="decline-banner" className="mt-3 px-3 py-3 bg-blood/10 border border-blood/40 text-xs">
                <div className="text-blood font-medium uppercase tracking-wider mb-1">Role Declined</div>
                <p className="text-[#A19D94]">{a.full_name} declined their role offer{decline.role_name ? <> for <span className="text-[#F2EBE1]">{decline.role_name}</span></> : null}.</p>
                {decline.reason ? <p className="text-[#D4CEC4] mt-1.5 italic">"{decline.reason}"</p> : <p className="text-[#6B6862] mt-1.5 italic">No reason given.</p>}
                {!isArchived && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => resolveDecline("remove")} data-testid="decline-remove-btn"
                      className="flex-1 py-2 bg-blood text-white text-[11px] uppercase tracking-wider hover:bg-[#FF3333]">Remove Role &amp; Dates</button>
                    <button onClick={() => resolveDecline("reoffer")} data-testid="decline-reoffer-btn"
                      className="flex-1 py-2 border border-[#262626] text-[#A19D94] text-[11px] uppercase tracking-wider hover:border-[#3a3835] hover:text-[#F2EBE1]">Offer New Role</button>
                  </div>
                )}
              </div>
            )}

            {isArchived ? (
              <p className="mt-4 text-xs text-[#6B6862] italic">This event is archived — read only.</p>
            ) : (
            <div className="mt-5 space-y-2">
              {/* Main action button — full state progression (Update 19 Change 01) */}
              {a.status === "contracted" ? (
                <ActionBtn variant="staticGreen" testid="status-action-btn"><span className="flex items-center justify-center gap-2"><Check size={14} /> Contracted</span></ActionBtn>
              ) : (a.status === "rejected" || a.status === "no_show") ? (
                <ActionBtn variant="staticRed" testid="status-action-btn">Declined</ActionBtn>
              ) : a.status === "offered_role" ? (
                <ActionBtn variant="blue" onClick={() => setTab("assign role")} testid="status-action-btn">Role Offered</ActionBtn>
              ) : a.status === "unable_to_attend" ? (
                <ActionBtn variant="redSolid" onClick={() => setShowCantAttend(true)} testid="status-action-btn">
                  <span className="flex items-center justify-center gap-2"><XIcon size={14} /> Can't Attend</span>
                </ActionBtn>
              ) : a.status === "audition_attended" ? (
                <ActionBtn variant="orange" onClick={() => setTab("assign role")} testid="status-action-btn">Assign Role</ActionBtn>
              ) : auditionAccepted ? (
                <ActionBtn variant="success" onClick={openInvite} testid="status-action-btn">
                  <span className="flex items-center justify-center gap-2"><Check size={14} /> Audition Accepted</span>
                </ActionBtn>
              ) : inviteSent ? (
                <ActionBtn variant="success" onClick={resendInvite} testid="status-action-btn">
                  <span className="flex items-center justify-center gap-2"><Check size={14} /> Invite Sent</span>
                </ActionBtn>
              ) : (
                <ActionBtn onClick={openInvite} testid="status-action-btn">Invite to Audition</ActionBtn>
              )}

              {/* Unable to attend — virtual audition option (Update 24 Change 03) */}
              {a.status === "unable_to_attend" && (
                <ActionBtn variant="orange" onClick={markVirtualAttended} testid="mark-virtual-btn">Mark as Virtually Attended</ActionBtn>
              )}

              <ActionBtn variant="outline" onClick={() => setTab("messages")}>Send Message</ActionBtn>
              {canSendContract && (
                a.contract_accepted_at ? (
                  <ActionBtn variant="staticGreen" testid="send-contract-btn">
                    <span className="flex items-center justify-center gap-2"><Check size={14} /> Contract Accepted</span>
                  </ActionBtn>
                ) : (
                  <ActionBtn variant={a.contract_sent_at ? "outline" : "primary"} onClick={sendContract} testid="send-contract-btn">
                    {a.contract_sent_at ? "Resend Contract" : "Send Contract"}
                  </ActionBtn>
                )
              )}
              <ActionBtn variant="ghost" onClick={rejectApplication} testid="reject-btn">Reject Application</ActionBtn>
            </div>
            )}
          </div>

          {/* Availability calendar — placed below Status & Actions per Change 04 */}
          <AvailabilityCalendar
            showDates={showDates}
            availableIds={a.available_dates || []}
            editable={!isArchived}
            onSave={async (ids) => { await adminApi.patch(`/admin/applicants/${a.id}`, { available_dates: ids }); await load(); }}
          />
        </div>
      </div>
      )}

      {/* Invite to Audition modal (Update 10) */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => !inviting && setShowInvite(false)} data-testid="invite-modal">
          <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[#1a1a1a]">
              <h3 className="font-display text-xl uppercase tracking-tight">Invite to Audition</h3>
              <p className="text-sm text-[#A19D94] mt-1">Select the audition date(s) to invite <span className="text-[#F2EBE1]">{a.full_name}</span> to. Choosing multiple lets them pick one.</p>
            </div>
            <div className="p-5 space-y-2">
              {futureAuditions.length === 0 && (
                <p className="text-sm text-[#6B6862]">No upcoming audition dates exist. Create one under <Link to="/admin/auditions" className="text-blood underline">Auditions</Link> first.</p>
              )}
              {futureAuditions.map((au) => {
                const sel = inviteSel.includes(au.id);
                return (
                  <button key={au.id} type="button" onClick={() => toggleInvite(au.id)} data-testid={`invite-date-${au.id}`}
                    className={`w-full text-left p-4 border transition-colors ${sel ? "border-blood bg-blood/5" : "border-[#262626] hover:border-[#3a3835]"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-display uppercase tracking-tight">{au.name}</div>
                        <div className="text-xs text-[#A19D94] mt-1">{formatLong(au.date)} · {au.start_time}–{au.end_time}</div>
                        {au.location && <div className="text-xs text-[#A19D94] mt-0.5">{au.location}</div>}
                      </div>
                      <span className={`w-5 h-5 shrink-0 border flex items-center justify-center ${sel ? "border-blood bg-blood text-white" : "border-[#3a3835]"}`}>
                        {sel && <Check size={12} />}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="p-5 border-t border-[#1a1a1a] flex justify-end gap-2">
              <button onClick={() => setShowInvite(false)} disabled={inviting} data-testid="invite-cancel-btn" className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#3a3835] disabled:opacity-50">Cancel</button>
              <button onClick={sendInvite} disabled={inviting || inviteSel.length === 0} data-testid="invite-send-btn" className="px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40 disabled:cursor-not-allowed">
                {inviting ? "Sending…" : `Send Invite${inviteSel.length ? ` (${inviteSel.length})` : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Can't Attend modal (Update 24 Change 02/03) */}
      {showCantAttend && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowCantAttend(false)} data-testid="cant-attend-modal">
          <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[#1a1a1a]">
              <h3 className="font-display text-xl uppercase tracking-tight flex items-center gap-2"><XIcon size={18} className="text-blood" /> Unable to Attend</h3>
              <p className="text-sm text-[#A19D94] mt-2">{a.full_name} has indicated they are unable to attend their audition.</p>
            </div>
            <div className="p-5 space-y-2">
              <button onClick={() => { setShowCantAttend(false); resendInvite(); }} data-testid="cant-attend-resend-btn"
                className="w-full px-4 py-2.5 text-sm uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood transition-colors">Resend Invite</button>
              <button onClick={markVirtualAttended} data-testid="cant-attend-virtual-btn"
                className="w-full px-4 py-2.5 text-sm uppercase tracking-wider bg-[#F97316] text-white hover:bg-[#EA580C] transition-colors">Mark as Virtually Attended</button>
            </div>
            <div className="p-5 border-t border-[#1a1a1a] flex justify-end">
              <button onClick={() => setShowCantAttend(false)} data-testid="cant-attend-close-btn" className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#3a3835]">Close</button>
            </div>
          </div>
        </div>
      )}
      {prefAssignRole && (
        <AssignDatesModal
          actor={{ id: a.id, full_name: a.full_name, available_dates: a.available_dates || [] }}
          role={prefAssignRole}
          showDates={showDates}
          takenDateIds={(prefAssignRole.assignments || []).map((x) => x.show_date_id)}
          occupiedByOther={roles.reduce((acc, r) => {
            if (r.id === prefAssignRole.id) return acc;
            (r.assignments || []).forEach((x) => { if (x.actor_id === a.id) acc[x.show_date_id] = r.name; });
            return acc;
          }, {})}
          onConfirm={confirmPrefAssign}
          onClose={() => setPrefAssignRole(null)}
        />
      )}

    </AdminLayout>
  );
}

const InfoRow = ({ label, value, mono, multiline }) => (
  <div className={multiline ? "" : "flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5"}>
    <span className="label-mini break-words">{label}</span>
    <span className={`text-sm text-[#F2EBE1] min-w-0 ${mono ? "font-mono" : ""} ${multiline ? "block mt-1 whitespace-pre-wrap break-words" : "text-right break-words"}`}>{value || "—"}</span>
  </div>
);

const ActionBtn = ({ children, onClick, variant = "primary", testid }) => {
  const base = "w-full px-4 py-2.5 text-sm uppercase tracking-wider transition-colors";
  const styles = {
    primary: "bg-blood text-white hover:bg-[#FF3333]",
    success: "bg-[#16A34A] text-white hover:bg-[#15803D]",
    orange: "bg-[#F97316] text-white hover:bg-[#EA580C]",
    blue: "bg-[#2563EB] text-white hover:bg-[#1D4ED8]",
    redSolid: "bg-[#B91C1C] text-white hover:bg-[#DC2626]",
    staticGreen: "bg-[#16A34A] text-white cursor-default",
    staticRed: "bg-[#7F1D1D] text-white cursor-default",
    outline: "border border-[#262626] text-[#A19D94] hover:border-blood hover:text-blood",
    ghost: "text-[#A19D94] hover:text-blood",
  };
  return <button onClick={onClick} data-testid={testid} className={`${base} ${styles[variant]}`}>{children}</button>;
};

function DynamicApplicationView({ applicant, schema, editing, draft, updDraft, showDates, tabFilter }) {
  const source = editing ? draft : applicant;
  if (!schema) {
    return (
      <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-5 space-y-3">
        <h3 className="label-mini mb-2">Personal Information</h3>
        <InfoRow label="Full Name" value={applicant.full_name} />
        <InfoRow label="Email" value={applicant.email} />
      </div>
    );
  }

  // Filter fields by tab assignment.
  // - tabFilter === null  => Profile tab: only fields with no profile_tab
  // - tabFilter === "Bio" => Custom tab: only fields with profile_tab === "Bio"
  // Personal detail fields are already shown in the left-column "Personal Details"
  // box, so they are excluded from the Profile tab to avoid a duplicate panel.
  const PERSONAL_DETAIL_IDS = ["first_name", "last_name", "email", "phone", "gender", "date_of_birth", "home_address", "emergency_contact_name", "emergency_contact_phone"];
  const visibleFields = (schema.fields || []).filter((f) => {
    if (f.type === "text_block") return false;
    if (tabFilter === null) return !f.profile_tab && !PERSONAL_DETAIL_IDS.includes(f.id);
    return f.profile_tab === tabFilter;
  });

  if (visibleFields.length === 0) {
    return (
      <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-8 text-center">
        <p className="text-sm text-[#6B6862]">
          {tabFilter ? `No answers assigned to the "${tabFilter}" tab yet.` : "No profile fields configured."}
        </p>
      </div>
    );
  }

  // Group fields by section. Derive the section list robustly: prefer the form's
  // declared section order, but always fall back to the sections present on the
  // fields themselves so answers still render even if schema.sections is missing/null.
  const FALLBACK_SECTION = "General";
  const sectionOrder = (schema.sections && schema.sections.length > 0) ? [...schema.sections] : [];
  visibleFields.forEach((f) => {
    const s = f.section || FALLBACK_SECTION;
    if (!sectionOrder.includes(s)) sectionOrder.push(s);
  });
  const grouped = sectionOrder.map((s) => ({
    name: s,
    fields: visibleFields.filter((f) => (f.section || FALLBACK_SECTION) === s),
  })).filter((g) => g.fields.length > 0);

  return (
    <div className="space-y-4">
      {grouped.map((sec) => (
        <div key={sec.name} className="border border-[#1a1a1a] bg-[#0A0A0A] p-5">
          <h3 className="label-mini mb-3">{sec.name}</h3>
          <div className="space-y-4">
            {sec.fields.map((f) => (
              <FieldDisplay
                key={f.id}
                field={f}
                value={source[f.id]}
                editing={editing}
                onChange={(v) => updDraft(f.id, v)}
                showDates={showDates}
                long={!!tabFilter /* custom tabs render long */}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FieldDisplay({ field, value, editing, onChange, showDates, long = false }) {
  if (editing) {
    return (
      <div>
        <label className="label-mini block mb-1.5">{field.label}{field.required && " *"}</label>
        <FieldRenderer field={field} value={value} onChange={onChange} showDates={showDates} />
      </div>
    );
  }
  // Read-only display — honor field.profile_display ("inline" | "new_line"), with smart defaults
  const display = renderReadValue(field, value);
  const SHORT_TYPES = ["short_text", "email", "dropdown", "number", "yes_no", "date"];
  const LONG_TYPES  = ["long_text", "file_upload", "availability_calendar", "multiple_choice", "text_block"];
  let layoutMode = field.profile_display; // "inline" | "new_line" | undefined
  if (layoutMode !== "inline" && layoutMode !== "new_line") {
    layoutMode = LONG_TYPES.includes(field.type) ? "new_line"
              : SHORT_TYPES.includes(field.type) ? "inline"
              : "inline";
  }
  if (long) layoutMode = "new_line"; // custom tab pages render long
  if (layoutMode === "new_line") {
    return (
      <div>
        <div className="label-mini mb-1">{field.label}</div>
        <div className="text-sm text-[#F2EBE1] whitespace-pre-wrap break-words leading-relaxed">{display}</div>
      </div>
    );
  }
  // inline — wrap gracefully if long
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
      <span className="label-mini break-words">{field.label}</span>
      <span className="text-sm text-[#F2EBE1] text-right break-words min-w-0">{display}</span>
    </div>
  );
}

function renderReadValue(field, v) {
  if (v === undefined || v === null || v === "") return "—";
  if (field.type === "yes_no") return v === true ? "Yes" : v === false ? "No" : "—";
  if (field.type === "checkbox") return v ? "✓ Yes" : "—";
  if (field.type === "file_upload") {
    return (
      <a href={fileUrl(v)} target="_blank" rel="noopener noreferrer" className="text-blood hover:underline inline-flex items-center gap-1">
        View file <ExternalLink size={11} />
      </a>
    );
  }
  if (field.type === "availability_calendar" && Array.isArray(v)) {
    if (v.length === 0) return "—";
    return <span className="font-mono text-xs">{v.length} date{v.length === 1 ? "" : "s"} selected</span>;
  }
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}


// --- Assign Role tab (Update 06 — Change 01) ---
function AssignRoleTab({ applicant, roles, showDates, reloadRoles, reloadApplicant, isArchived }) {
  const dialog = useDialog();
  const { formatDate } = useSettings();
  const [assignRole, setAssignRole] = useState(null);

  const assignmentsFor = (r) => (r.assignments || []).filter((a) => a.actor_id === applicant.id);
  const myRoles = roles.filter((r) => assignmentsFor(r).length > 0);

  const confirmAssign = async ({ date_ids, send_email }) => {
    await adminApi.post(`/admin/roles/${assignRole.id}/assign-dates`, { actor_id: applicant.id, date_ids, send_email });
    setAssignRole(null);
    reloadRoles();
    reloadApplicant && reloadApplicant();
  };
  const removeFromRole = async (r) => {
    if (await dialog.confirm({ title: `Remove from ${r.name}?`, description: `Removes ${applicant.full_name} from this role on all dates.`, confirmLabel: "Remove", destructive: true })) {
      await adminApi.post(`/admin/roles/${r.id}/unassign-actor`, { actor_id: applicant.id });
      reloadRoles();
    }
  };
  const dateName = (id) => { const sd = showDates.find((x) => x.id === id); return sd ? formatDate(sd.date) : id; };
  const canAssign = ["offered_role", "contracted", "audition_attended"].includes(applicant.status);

  return (
    <div className="space-y-4" data-testid="assign-role-tab">
      {!canAssign && (
        <div data-testid="assign-not-eligible" className="border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] text-sm px-4 py-3">
          {applicant.full_name} must be at status <strong>Offered a Role</strong> or above before they can be assigned to a role.
        </div>
      )}
      <div className="border border-blood/40 bg-blood/5 p-4">
        <div className="label-mini text-blood mb-2">Current Assignments</div>
        {myRoles.length === 0 ? (
          <p className="text-sm text-[#A19D94]">{applicant.full_name} is not assigned to any role yet.</p>
        ) : (
          <div className="space-y-2">
            {myRoles.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 px-3 py-2 border border-blood/40 bg-[#0A0A0A]">
                <div className="min-w-0">
                  <span className="font-display uppercase tracking-tight">{r.name}</span>
                  <span className="ml-2 text-[11px] font-mono text-[#A19D94]">{assignmentsFor(r).map((a) => dateName(a.show_date_id)).join(", ")}</span>
                </div>
                {!isArchived && <button onClick={() => removeFromRole(r)} data-testid={`assign-remove-${r.id}`} className="shrink-0 text-[#6B6862] hover:text-blood"><XIcon size={14} /></button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {roles.length === 0 ? (
        <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-8 text-center text-sm text-[#6B6862]">
          No roles defined yet. Create roles under <span className="text-[#F2EBE1]">Role Builder</span> first.
        </div>
      ) : (
        <div className="space-y-2" data-testid="assign-role-list">
          {roles.map((r) => {
            const mine = assignmentsFor(r);
            const totalAssigned = (r.assignments || []).length;
            const totalDates = showDates.length;
            const fill = totalAssigned > 0 ? `${totalAssigned} of ${totalDates} date${totalDates === 1 ? "" : "s"} filled` : "Vacant";
            return (
              <div key={r.id} data-testid={`assign-role-row-${r.id}`} className="flex items-center gap-3 border border-[#1a1a1a] bg-[#0A0A0A] px-3 py-2.5 hover:border-[#262626] transition-colors">
                <div className="w-11 h-11 shrink-0 border border-[#262626] bg-[#050505] overflow-hidden flex items-center justify-center">
                  {r.profile_image ? <img src={fileUrl(r.profile_image)} alt={r.name} className="w-full h-full object-cover" /> : <ImageIcon size={16} className="text-[#3a3835]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display uppercase tracking-tight truncate leading-tight">{r.name}</div>
                  <div className="text-[11px] text-[#6B6862] mt-0.5">
                    {fill}
                    {mine.length > 0 && <span className="text-blood"> · {mine.length} for {applicant.first_name || "this actor"}</span>}
                  </div>
                </div>
                <div className="shrink-0">
                  {isArchived ? (
                    <span className="text-xs text-[#6B6862] italic">Read only</span>
                  ) : !canAssign ? (
                    <span className="text-xs text-[#6B6862] italic">Not eligible</span>
                  ) : (
                    <button onClick={() => setAssignRole(r)} data-testid={`assign-dates-btn-${r.id}`} className="px-3 py-1.5 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] inline-flex items-center gap-1.5">
                      <Check size={12} /> {mine.length > 0 ? "Add dates" : "Assign"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {assignRole && (
        <AssignDatesModal
          actor={{ id: applicant.id, full_name: applicant.full_name, available_dates: applicant.available_dates || [] }}
          role={assignRole}
          showDates={showDates}
          takenDateIds={(assignRole.assignments || []).map((a) => a.show_date_id)}
          occupiedByOther={roles.reduce((acc, r) => {
            if (r.id === assignRole.id) return acc;
            (r.assignments || []).forEach((a) => { if (a.actor_id === applicant.id) acc[a.show_date_id] = r.name; });
            return acc;
          }, {})}
          onConfirm={confirmAssign}
          onClose={() => setAssignRole(null)}
        />
      )}
    </div>
  );
}
